import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { isCoProducerEmail } from "@/lib/coProducerAccess";
import { describeSubscriptionPlan } from "@produce/shared";
import { Link } from "react-router-dom";
import AdminSidebar from "@/components/AdminSidebar";
import { SidebarProvider } from "@/components/ui/sidebar";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Search,
  Shield,
  User,
  Users,
  ChevronDown,
  ChevronRight,
  Calendar,
  CreditCard,
  Briefcase,
  Trash2,
  UserPlus,
  Eye,
} from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

interface Profile {
  id: string;
  email: string;
  full_name: string | null;
  company_name: string | null;
  manual_producer_access: boolean;
  invoice_information: string | null;
}

interface Subscription {
  user_id: string;
  plan_type: string | null;
  plan_tier: string | null;
  plan_status: string;
  subscription_start: string | null;
  subscription_end: string | null;
  trial_ends_at: string | null;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  discount_code_used: string | null;
  discount_end_date: string | null;
  stripe_discount_id: string | null;
}

export default function UserManagement() {
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [createUserOpen, setCreateUserOpen] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState("");
  const [newUserPassword, setNewUserPassword] = useState("");
  const [newUserFullName, setNewUserFullName] = useState("");
  const [newUserCompany, setNewUserCompany] = useState("");
  const [newUserTier, setNewUserTier] = useState<"lite" | "pro" | "studio">("lite");
  const queryClient = useQueryClient();

  const { data: profiles = [] } = useQuery({
    queryKey: ["all-profiles"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) {
        toast.error("Failed to load profiles: " + error.message);
        throw error;
      }
      // Hide the hidden co-producer accounts — they're not real platform users
      // and would skew counts / search results.
      return (data as Profile[]).filter((p) => !isCoProducerEmail(p.email));
    },
  });

  const { data: userRoles = {} } = useQuery({
    queryKey: ["all-user-roles"],
    queryFn: async () => {
      const { data, error } = await supabase.from("user_roles").select("user_id, role");

      if (error) throw error;

      const rolesMap: Record<string, string[]> = {};
      data.forEach((ur: { user_id: string; role: string }) => {
        if (!rolesMap[ur.user_id]) rolesMap[ur.user_id] = [];
        rolesMap[ur.user_id].push(ur.role);
      });
      return rolesMap;
    },
  });

  // Fetch all briefings and group by producer
  const { data: briefingsByProducer = {} } = useQuery({
    queryKey: ["all-briefings-by-producer"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("briefings")
        .select("id, festival_name, status, producer_id, festival_date_start");

      if (error) throw error;

      const briefingsMap: Record<
        string,
        {
          id: string;
          festival_name: string | null;
          status: string;
          producer_id: string | null;
          festival_date_start: string | null;
        }[]
      > = {};
      data.forEach(
        (briefing: {
          id: string;
          festival_name: string | null;
          status: string;
          producer_id: string | null;
          festival_date_start: string | null;
        }) => {
          const key = briefing.producer_id ?? "unknown";
          if (!briefingsMap[key]) briefingsMap[key] = [];
          briefingsMap[key].push(briefing);
        },
      );
      return briefingsMap;
    },
  });

  // Fetch all subscriptions
  const { data: subscriptions = {} } = useQuery({
    queryKey: ["all-subscriptions"],
    queryFn: async () => {
      const { data, error } = await supabase.from("producer_subscriptions").select("*");

      if (error) throw error;

      const subsMap: Record<string, Subscription> = {};
      data.forEach((sub: Subscription & { user_id: string }) => {
        subsMap[sub.user_id] = sub;
      });
      return subsMap;
    },
  });

  const toggleRow = (userId: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(userId)) {
      newExpanded.delete(userId);
    } else {
      newExpanded.add(userId);
    }
    setExpandedRows(newExpanded);
  };

  const toggleManualAccess = useMutation({
    mutationFn: async ({ userId, newValue }: { userId: string; newValue: boolean }) => {
      const { error } = await supabase
        .from("profiles")
        .update({ manual_producer_access: newValue })
        .eq("id", userId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["all-profiles"] });
      toast.success("Manual access updated");
    },
    onError: (error) => {
      toast.error("Failed to update access: " + error.message);
    },
  });

  const addRole = useMutation({
    mutationFn: async ({
      userId,
      role,
    }: {
      userId: string;
      role: "admin" | "producer" | "crew";
    }) => {
      const { error } = await supabase.from("user_roles").insert({ user_id: userId, role });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["all-user-roles"] });
      toast.success("Role added");
    },
    onError: (error) => {
      toast.error("Failed to add role: " + error.message);
    },
  });

  const removeRole = useMutation({
    mutationFn: async ({
      userId,
      role,
    }: {
      userId: string;
      role: "admin" | "producer" | "crew";
    }) => {
      const { error } = await supabase
        .from("user_roles")
        .delete()
        .eq("user_id", userId)
        .eq("role", role);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["all-user-roles"] });
      toast.success("Role removed");
    },
    onError: (error) => {
      toast.error("Failed to remove role: " + error.message);
    },
  });

  const [roleWarningOpen, setRoleWarningOpen] = useState(false);
  const [adminWarningOpen, setAdminWarningOpen] = useState(false);
  const [deleteUserWarningOpen, setDeleteUserWarningOpen] = useState(false);
  const [pendingRoleRemoval, setPendingRoleRemoval] = useState<{
    userId: string;
    role: "admin" | "producer" | "crew";
  } | null>(null);
  const [pendingRoleAddition, setPendingRoleAddition] = useState<{
    userId: string;
    role: "admin" | "producer" | "crew";
  } | null>(null);
  const [pendingUserDeletion, setPendingUserDeletion] = useState<{
    userId: string;
    email: string;
  } | null>(null);

  const handleRoleToggle = (
    userId: string,
    role: "admin" | "producer" | "crew",
    currentlyHasRole: boolean,
  ) => {
    if (currentlyHasRole) {
      // Removing role
      if (role === "producer") {
        setPendingRoleRemoval({ userId, role });
        setRoleWarningOpen(true);
      } else {
        removeRole.mutate({ userId, role });
      }
    } else {
      // Adding role
      if (role === "admin") {
        setPendingRoleAddition({ userId, role });
        setAdminWarningOpen(true);
      } else {
        addRole.mutate({ userId, role });
      }
    }
  };

  const confirmRoleRemoval = () => {
    if (pendingRoleRemoval) {
      removeRole.mutate(pendingRoleRemoval);
      setPendingRoleRemoval(null);
      setRoleWarningOpen(false);
    }
  };

  const confirmAdminAddition = () => {
    if (pendingRoleAddition) {
      addRole.mutate(pendingRoleAddition);
      setPendingRoleAddition(null);
      setAdminWarningOpen(false);
    }
  };

  const deleteUser = useMutation({
    mutationFn: async (userId: string) => {
      // Call admin-delete-user edge function
      const { data, error } = await supabase.functions.invoke("admin-delete-user", {
        body: { targetUserId: userId },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["all-profiles"] });
      queryClient.invalidateQueries({ queryKey: ["all-user-roles"] });
      queryClient.invalidateQueries({ queryKey: ["all-subscriptions"] });
      queryClient.invalidateQueries({ queryKey: ["all-briefings-by-producer"] });
      toast.success("User deleted successfully");
      setPendingUserDeletion(null);
      setDeleteUserWarningOpen(false);
    },
    onError: (error) => {
      toast.error("Failed to delete user: " + error.message);
    },
  });

  const handleDeleteUser = (userId: string, email: string) => {
    setPendingUserDeletion({ userId, email });
    setDeleteUserWarningOpen(true);
  };

  const confirmUserDeletion = () => {
    if (pendingUserDeletion) {
      deleteUser.mutate(pendingUserDeletion.userId);
    }
  };

  const createUser = useMutation({
    mutationFn: async (data: {
      email: string;
      password: string;
      fullName: string;
      companyName?: string;
      planTier: "lite" | "pro" | "studio";
    }) => {
      const { data: result, error } = await supabase.functions.invoke("admin-create-user", {
        body: data,
      });

      if (error) throw error;
      if (result?.error) throw new Error(result.error);

      return result;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["all-profiles"] });
      queryClient.invalidateQueries({ queryKey: ["all-user-roles"] });
      queryClient.invalidateQueries({ queryKey: ["all-subscriptions"] });
      toast.success(data.message || "User created successfully");
      setCreateUserOpen(false);
      // Reset form
      setNewUserEmail("");
      setNewUserPassword("");
      setNewUserFullName("");
      setNewUserCompany("");
      setNewUserTier("lite");
    },
    onError: (error) => {
      toast.error("Failed to create user: " + error.message);
    },
  });

  const handleCreateUser = () => {
    if (!newUserEmail || !newUserPassword || !newUserFullName) {
      toast.error("Please fill in all required fields");
      return;
    }
    createUser.mutate({
      email: newUserEmail,
      password: newUserPassword,
      fullName: newUserFullName,
      companyName: newUserCompany || undefined,
      planTier: newUserTier,
    });
  };

  const filteredProfiles = profiles.filter(
    (p) =>
      p.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.company_name?.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const getRoleIcon = (role: string) => {
    switch (role) {
      case "admin":
        return <Shield className="h-3 w-3" />;
      case "producer":
        return <Users className="h-3 w-3" />;
      case "crew":
        return <User className="h-3 w-3" />;
      default:
        return null;
    }
  };

  const formatDate = (date: string | null) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    });
  };

  const getDaysUntil = (date: string | null) => {
    if (!date) return null;
    const target = new Date(date);
    const now = new Date();
    const diffTime = target.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getSubscriptionStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive"> = {
      active: "default",
      trial: "secondary",
      canceled: "destructive",
      expired: "destructive",
    };
    return variants[status] || "secondary";
  };

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full bg-background">
        <AdminSidebar />
        <div className="flex-1 p-8">
          <div className="mx-auto max-w-7xl space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="mb-2 text-3xl font-bold">User Management</h1>
                <p className="text-muted-foreground">Manage user roles and access permissions</p>
              </div>
              <Button onClick={() => setCreateUserOpen(true)}>
                <UserPlus className="mr-2 h-4 w-4" />
                Create User
              </Button>
            </div>

            <Card className="p-6">
              <div className="mb-6">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-muted-foreground" />
                  <Input
                    placeholder="Search users by email, name, or company..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8"></TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Company</TableHead>
                    <TableHead>Roles</TableHead>
                    <TableHead>Subscription</TableHead>
                    <TableHead>Productions</TableHead>
                    <TableHead>Manual Access</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredProfiles.map((profile) => {
                    const isExpanded = expandedRows.has(profile.id);
                    const subscription = subscriptions[profile.id];
                    const productions = briefingsByProducer[profile.id] || [];
                    const hasProducerRole = (userRoles[profile.id] || []).includes("producer");

                    return (
                      <>
                        <TableRow key={profile.id}>
                          <TableCell>
                            {hasProducerRole && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                onClick={() => toggleRow(profile.id)}
                              >
                                {isExpanded ? (
                                  <ChevronDown className="h-4 w-4" />
                                ) : (
                                  <ChevronRight className="h-4 w-4" />
                                )}
                              </Button>
                            )}
                          </TableCell>
                          <TableCell>
                            <div>
                              <Link
                                to={`/admin/users/${profile.id}`}
                                className="font-medium hover:underline"
                              >
                                {profile.full_name || "No name"}
                              </Link>
                              <div className="text-sm text-muted-foreground">{profile.email}</div>
                            </div>
                          </TableCell>
                          <TableCell>{profile.company_name || "-"}</TableCell>
                          <TableCell>
                            <div className="flex flex-col gap-2">
                              {(["admin", "producer", "crew"] as const).map((role) => {
                                const hasRole = (userRoles[profile.id] || []).includes(role);
                                return (
                                  <div key={role} className="flex items-center gap-2">
                                    <Checkbox
                                      id={`${profile.id}-${role}`}
                                      checked={hasRole}
                                      onCheckedChange={() =>
                                        handleRoleToggle(profile.id, role, hasRole)
                                      }
                                    />
                                    <label
                                      htmlFor={`${profile.id}-${role}`}
                                      className="flex cursor-pointer items-center gap-1 text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                    >
                                      {getRoleIcon(role)}
                                      {role}
                                    </label>
                                  </div>
                                );
                              })}
                            </div>
                          </TableCell>
                          <TableCell>
                            {subscription ? (
                              <div className="flex flex-col gap-1">
                                <Badge
                                  variant={getSubscriptionStatusBadge(subscription.plan_status)}
                                >
                                  {subscription.plan_status}
                                </Badge>
                                {subscription.plan_tier && (
                                  <span className="text-xs font-medium text-primary">
                                    {subscription.plan_tier.toUpperCase()}
                                  </span>
                                )}
                                <span className="text-xs text-muted-foreground">
                                  {describeSubscriptionPlan(subscription) ?? "-"}
                                </span>
                              </div>
                            ) : hasProducerRole ? (
                              <span className="text-sm text-muted-foreground">No subscription</span>
                            ) : (
                              <span className="text-sm text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {hasProducerRole ? (
                              <Badge variant="outline">
                                {productions.length} production{productions.length !== 1 ? "s" : ""}
                              </Badge>
                            ) : (
                              <span className="text-sm text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Button
                              size="sm"
                              variant={profile.manual_producer_access ? "default" : "outline"}
                              onClick={() =>
                                toggleManualAccess.mutate({
                                  userId: profile.id,
                                  newValue: !profile.manual_producer_access,
                                })
                              }
                            >
                              {profile.manual_producer_access ? "Enabled" : "Disabled"}
                            </Button>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Button size="sm" variant="outline" asChild title="Open user details">
                                <Link to={`/admin/users/${profile.id}`}>
                                  <Eye className="h-4 w-4" />
                                </Link>
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleDeleteUser(profile.id, profile.email)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>

                        {/* Expanded Details Row */}
                        {isExpanded && hasProducerRole && (
                          <TableRow>
                            <TableCell colSpan={8} className="bg-muted/50">
                              <div className="space-y-6 p-4">
                                {/* Subscription Details */}
                                <div>
                                  <h3 className="mb-3 flex items-center gap-2 font-semibold">
                                    <CreditCard className="h-4 w-4" />
                                    Subscription Details
                                  </h3>
                                  {subscription ? (
                                    <div className="space-y-4">
                                      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                                        <div>
                                          <p className="text-sm text-muted-foreground">Plan Type</p>
                                          <p className="font-medium">
                                            {describeSubscriptionPlan(subscription) ?? "Free"}
                                          </p>
                                        </div>
                                        <div>
                                          <p className="text-sm text-muted-foreground">Status</p>
                                          <Badge
                                            variant={getSubscriptionStatusBadge(
                                              subscription.plan_status,
                                            )}
                                          >
                                            {subscription.plan_status}
                                          </Badge>
                                        </div>
                                        <div>
                                          <p className="text-sm text-muted-foreground">
                                            Start Date
                                          </p>
                                          <p className="font-medium">
                                            {formatDate(subscription.subscription_start)}
                                          </p>
                                        </div>
                                        <div>
                                          <p className="text-sm text-muted-foreground">
                                            {subscription.plan_status === "trial"
                                              ? "Trial Ends"
                                              : "Renewal Date"}
                                          </p>
                                          <div className="flex items-center gap-2">
                                            <p className="font-medium">
                                              {subscription.plan_status === "trial"
                                                ? formatDate(subscription.trial_ends_at)
                                                : formatDate(subscription.subscription_end)}
                                            </p>
                                            {(() => {
                                              const days = getDaysUntil(
                                                subscription.plan_status === "trial"
                                                  ? subscription.trial_ends_at
                                                  : subscription.subscription_end,
                                              );
                                              if (days !== null && days >= 0) {
                                                return (
                                                  <Badge variant="outline" className="text-xs">
                                                    {days}d
                                                  </Badge>
                                                );
                                              }
                                              return null;
                                            })()}
                                          </div>
                                        </div>
                                      </div>

                                      {/* Discount Information */}
                                      {subscription.discount_code_used && (
                                        <div className="rounded-lg border border-primary/20 bg-primary/10 p-3">
                                          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                                            <div>
                                              <p className="text-sm text-muted-foreground">
                                                Discount Code
                                              </p>
                                              <p className="font-mono font-bold text-primary">
                                                {subscription.discount_code_used}
                                              </p>
                                            </div>
                                            {!subscription.discount_end_date ? (
                                              <div>
                                                <p className="text-sm text-muted-foreground">
                                                  Discount Duration
                                                </p>
                                                <Badge className="bg-purple-600">Lifetime</Badge>
                                              </div>
                                            ) : (
                                              <>
                                                <div>
                                                  <p className="text-sm text-muted-foreground">
                                                    Discount Ends
                                                  </p>
                                                  <div className="flex items-center gap-2">
                                                    <p className="font-medium">
                                                      {formatDate(subscription.discount_end_date)}
                                                    </p>
                                                    {(() => {
                                                      const days = getDaysUntil(
                                                        subscription.discount_end_date,
                                                      );
                                                      if (days !== null && days >= 0) {
                                                        return (
                                                          <Badge
                                                            variant="outline"
                                                            className="text-xs"
                                                          >
                                                            {days}d left
                                                          </Badge>
                                                        );
                                                      }
                                                      return (
                                                        <Badge
                                                          variant="destructive"
                                                          className="text-xs"
                                                        >
                                                          Expired
                                                        </Badge>
                                                      );
                                                    })()}
                                                  </div>
                                                </div>
                                              </>
                                            )}
                                            {!subscription.discount_end_date && (
                                              <div>
                                                <p className="text-sm text-muted-foreground">
                                                  Duration
                                                </p>
                                                <Badge variant="default">Lifetime</Badge>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      )}

                                      {/* Stripe Links */}
                                      {subscription.stripe_customer_id && (
                                        <div className="flex gap-2 pt-2">
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() =>
                                              window.open(
                                                `https://dashboard.stripe.com/customers/${subscription.stripe_customer_id}`,
                                                "_blank",
                                              )
                                            }
                                          >
                                            View in Stripe
                                          </Button>
                                          {subscription.stripe_subscription_id && (
                                            <Button
                                              size="sm"
                                              variant="outline"
                                              onClick={() =>
                                                window.open(
                                                  `https://dashboard.stripe.com/subscriptions/${subscription.stripe_subscription_id}`,
                                                  "_blank",
                                                )
                                              }
                                            >
                                              View Subscription
                                            </Button>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    <p className="text-sm text-muted-foreground">
                                      No subscription found
                                    </p>
                                  )}
                                </div>

                                {/* Billing Information */}
                                <div>
                                  <h3 className="mb-3 flex items-center gap-2 font-semibold">
                                    <Calendar className="h-4 w-4" />
                                    Billing Information
                                  </h3>
                                  {profile.invoice_information ? (
                                    <div className="rounded-md border bg-background p-3">
                                      <p className="whitespace-pre-wrap text-sm">
                                        {profile.invoice_information}
                                      </p>
                                    </div>
                                  ) : (
                                    <p className="text-sm text-muted-foreground">
                                      No billing information provided
                                    </p>
                                  )}
                                </div>

                                {/* Productions List */}
                                <div>
                                  <h3 className="mb-3 flex items-center gap-2 font-semibold">
                                    <Briefcase className="h-4 w-4" />
                                    Productions ({productions.length})
                                  </h3>
                                  {productions.length > 0 ? (
                                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                                      {productions.map((production) => (
                                        <div
                                          key={production.id}
                                          className="rounded-md border bg-background p-3"
                                        >
                                          <div className="flex items-start justify-between">
                                            <div className="min-w-0 flex-1">
                                              <p className="truncate font-medium">
                                                {production.festival_name || "Untitled"}
                                              </p>
                                              <p className="text-sm text-muted-foreground">
                                                {formatDate(production.festival_date_start)}
                                              </p>
                                            </div>
                                            <Badge
                                              variant={
                                                production.status === "published"
                                                  ? "default"
                                                  : "secondary"
                                              }
                                              className="ml-2"
                                            >
                                              {production.status}
                                            </Badge>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <p className="text-sm text-muted-foreground">
                                      No productions yet
                                    </p>
                                  )}
                                </div>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </>
                    );
                  })}
                </TableBody>
              </Table>
            </Card>
          </div>
        </div>
      </div>

      <AlertDialog open={roleWarningOpen} onOpenChange={setRoleWarningOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Waarschuwing: Producer Role Verwijderen</AlertDialogTitle>
            <AlertDialogDescription>
              Let op! Dit kan een betaalde klant zijn. Het verwijderen van de producer role betekent
              dat deze gebruiker geen toegang meer heeft tot het producer portal, tenzij manual
              access is ingeschakeld.
              <br />
              <br />
              Weet je zeker dat je de producer role wilt verwijderen?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingRoleRemoval(null)}>
              Annuleren
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmRoleRemoval}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={adminWarningOpen} onOpenChange={setAdminWarningOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Add Admin Role</AlertDialogTitle>
            <AlertDialogDescription>
              You are about to grant admin privileges to this user. Admin users have full access to
              all features and can manage other users.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setPendingRoleAddition(null);
                setAdminWarningOpen(false);
              }}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmAdminAddition}>Confirm</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteUserWarningOpen} onOpenChange={setDeleteUserWarningOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-destructive">Delete User Account</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p className="font-semibold">⚠️ This action cannot be undone!</p>
              <p>
                Deleting user{" "}
                <span className="font-mono font-bold">{pendingUserDeletion?.email}</span> will:
              </p>
              <ul className="list-inside list-disc space-y-1 text-sm">
                <li>Cancel their active Stripe subscription immediately</li>
                <li>Delete all their productions and associated data</li>
                <li>Remove all crew members from their productions</li>
                <li>Delete their account permanently</li>
              </ul>
              <p className="mt-4 font-semibold text-destructive">Are you absolutely sure?</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setPendingUserDeletion(null);
                setDeleteUserWarningOpen(false);
              }}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmUserDeletion}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete User
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Create User Dialog */}
      <Dialog open={createUserOpen} onOpenChange={setCreateUserOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Create New User</DialogTitle>
            <DialogDescription>
              Create a producer account with a specific subscription tier. The user can log in
              immediately.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                placeholder="user@example.com"
                value={newUserEmail}
                onChange={(e) => setNewUserEmail(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password *</Label>
              <Input
                id="password"
                type="text"
                placeholder="Temporary password"
                value={newUserPassword}
                onChange={(e) => setNewUserPassword(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="fullName">Full Name *</Label>
              <Input
                id="fullName"
                placeholder="John Doe"
                value={newUserFullName}
                onChange={(e) => setNewUserFullName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="company">Company</Label>
              <Input
                id="company"
                placeholder="Company name (optional)"
                value={newUserCompany}
                onChange={(e) => setNewUserCompany(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="tier">Subscription Tier *</Label>
              <Select
                value={newUserTier}
                onValueChange={(v: "lite" | "pro" | "studio") => setNewUserTier(v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select tier" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="lite">LITE</SelectItem>
                  <SelectItem value="pro">PRO</SelectItem>
                  <SelectItem value="studio">STUDIO</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateUserOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateUser} disabled={createUser.isPending}>
              {createUser.isPending ? "Creating..." : "Create User"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SidebarProvider>
  );
}
